<template>
  <div class="survival">
    <div class="bg-decoration">
      <svg class="molecule-bg molecule-bg-1" viewBox="0 0 400 400" fill="none">
        <circle cx="200" cy="80" r="6" fill="#fca5a5" opacity="0.6"/>
        <circle cx="120" cy="140" r="5" fill="#fca5a5" opacity="0.5"/>
        <circle cx="280" cy="140" r="7" fill="#fca5a5" opacity="0.7"/>
        <circle cx="80" cy="220" r="4" fill="#fca5a5" opacity="0.4"/>
        <circle cx="200" cy="200" r="8" fill="#fca5a5" opacity="0.8"/>
        <circle cx="320" cy="220" r="5" fill="#fca5a5" opacity="0.5"/>
        <circle cx="150" cy="280" r="6" fill="#fca5a5" opacity="0.6"/>
        <circle cx="250" cy="280" r="5" fill="#fca5a5" opacity="0.5"/>
        <circle cx="200" cy="350" r="4" fill="#fca5a5" opacity="0.4"/>
        <line x1="200" y1="80" x2="120" y2="140" stroke="#fca5a5" stroke-width="1" opacity="0.3"/>
        <line x1="200" y1="80" x2="280" y2="140" stroke="#fca5a5" stroke-width="1" opacity="0.3"/>
        <line x1="120" y1="140" x2="80" y2="220" stroke="#fca5a5" stroke-width="1" opacity="0.3"/>
        <line x1="120" y1="140" x2="200" y2="200" stroke="#fca5a5" stroke-width="1" opacity="0.3"/>
        <line x1="280" y1="140" x2="200" y2="200" stroke="#fca5a5" stroke-width="1" opacity="0.3"/>
        <line x1="280" y1="140" x2="320" y2="220" stroke="#fca5a5" stroke-width="1" opacity="0.3"/>
        <line x1="200" y1="200" x2="150" y2="280" stroke="#fca5a5" stroke-width="1" opacity="0.3"/>
        <line x1="200" y1="200" x2="250" y2="280" stroke="#fca5a5" stroke-width="1" opacity="0.3"/>
        <line x1="150" y1="280" x2="200" y2="350" stroke="#fca5a5" stroke-width="1" opacity="0.3"/>
        <line x1="250" y1="280" x2="200" y2="350" stroke="#fca5a5" stroke-width="1" opacity="0.3"/>
      </svg>

      <svg class="dna-bg" viewBox="0 0 200 400" fill="none">
        <path d="M40 20 Q100 60 40 100 Q-20 140 40 180 Q100 220 40 260 Q-20 300 40 340 Q100 380 40 420"
              stroke="#f87171" stroke-width="2" fill="none" opacity="0.2"/>
        <path d="M160 20 Q100 60 160 100 Q220 140 160 180 Q100 220 160 260 Q220 300 160 340 Q100 380 160 420"
              stroke="#f87171" stroke-width="2" fill="none" opacity="0.2"/>
        <line x1="60" y1="60" x2="140" y2="60" stroke="#f87171" stroke-width="1" opacity="0.15"/>
        <line x1="50" y1="100" x2="150" y2="100" stroke="#f87171" stroke-width="1" opacity="0.15"/>
        <line x1="60" y1="140" x2="140" y2="140" stroke="#f87171" stroke-width="1" opacity="0.15"/>
        <line x1="50" y1="180" x2="150" y2="180" stroke="#f87171" stroke-width="1" opacity="0.15"/>
      </svg>
    </div>

    <div class="survival-content">
      <div class="header-section">
        <h1 class="page-title">Disease Risk Stratification</h1>
        <p class="page-subtitle">Browse Cox survival summary results by phenotype and sex.</p>
      </div>

      <div class="query-card">
        <p class="section-description">
          In the Survival Summary Table, users can select a disease category, specific disease endpoint, and sex group, then click Browse to retrieve the corresponding Cox proportional hazards regression results. The table summarizes the association between model-derived metabolomic risk scores and incident disease outcomes, including effect estimates, hazard ratios, confidence intervals and significance statistics, and the results can be downloaded for further analysis.
        </p>
        <div class="filters-section">
          <div class="filter-group">
            <label class="filter-label">Disease Category</label>
            <el-select v-model="selectedCategory" placeholder="Select category" class="filter-select" size="small" filterable>
              <el-option
                v-for="cat in categories"
                :key="cat"
                :label="cat"
                :value="cat">
              </el-option>
            </el-select>
          </div>
          <div class="filter-group">
            <label class="filter-label">Disease</label>
            <el-select
              v-model="selectedDisease"
              placeholder="Select disease"
              class="filter-select"
              size="small"
              filterable
              :filter-method="filterDiseases"
            >
              <el-option
                v-for="disease in filteredDiseases"
                :key="disease.phid"
                :label="disease.reportedTrait"
                :value="disease.phid">
              </el-option>
            </el-select>
          </div>
          <div class="filter-group">
            <label class="filter-label">Sex</label>
            <el-select v-model="sex" size="small" class="filter-select">
              <el-option label="All" value="all" />
              <el-option label="Female" value="female" />
              <el-option label="Male" value="male" />
            </el-select>
          </div>
          <div class="filter-actions">
            <el-button type="danger" size="small" class="search-btn" @click="handleSearch">
              <i class="el-icon-search"></i>
              Browse
            </el-button>
          </div>
        </div>
      </div>

      <div class="table-card">
        <div class="panel-header">
          <div>
            <div class="panel-title">Survival Summary Table</div>
          </div>
          <div class="result-actions">
            <div class="table-summary">
              Showing {{ tableData.length > 0 ? (currentPage - 1) * pageSize + 1 : 0 }}
              to {{ Math.min(currentPage * pageSize, total) }} of {{ total }} results
            </div>
            <el-button size="small" @click="handleDownloadSummary">Download</el-button>
          </div>
        </div>

        <el-table
          v-loading="tableLoading"
          :data="tableData"
          border
          stripe
          class="result-table"
          header-cell-class-name="table-header-cell"
          empty-text="No data"
          @sort-change="handleSortChange"
        >
          <el-table-column
            v-for="column in tableColumns"
            :key="column.key"
            :prop="column.key"
            :label="column.label"
            min-width="160"
            show-overflow-tooltip
            sortable="custom"
            :sort-orders="sortOrders"
          />
        </el-table>

        <div class="pagination-wrap">
          <el-pagination
            background
            layout="prev, pager, next"
            :current-page="currentPage"
            :page-size="pageSize"
            :total="total"
            @current-change="handlePageChange"
          />
        </div>
      </div>

      <div class="table-card grouping-resource-card">
        <p class="section-description">
          In the Grouping Resource section, users can select a predefined metabolomic risk score grouping method after choosing a disease endpoint. The platform then displays the corresponding group-based survival results, including Kaplan-Meier curves and comparisons among low-, intermediate-, and high-risk groups, allowing users to visualize differences in event-free survival and evaluate the risk-stratification performance of the metabolomic prediction model.
        </p>
        <div class="panel-header">
          <div>
            <div class="panel-title">Grouping Resource</div>
          </div>
          <div class="table-summary">Resources for the selected MRS grouping.</div>
        </div>

        <div class="grouping-select-row">
          <div class="filter-group filter-group-wide">
            <label class="filter-label">MRS Value Grouping Method</label>
            <el-select
              v-model="selectedGrouping"
              placeholder="Select grouping"
              size="small"
              class="filter-select"
              :disabled="!selectedDisease"
            >
              <el-option
                v-for="item in groupingOptions"
                :key="item.value"
                :label="item.label"
                :value="item.value"
              />
            </el-select>
          </div>
        </div>

        <div v-loading="groupResourceLoading" class="group-resource-wrap">
          <div v-if="groupResourceImages.length > 0" class="images-grid">
            <div class="image-card" v-for="(resource, index) in groupResourceImages" :key="resource.name || index">
              <div class="image-name">{{ resource.name || `Resource ${index + 1}` }}</div>
              <el-image
                class="result-image preview-image"
                :src="resource.src"
                :preview-src-list="groupResourcePreviewList"
                :initial-index="index"
                fit="contain"
                @error="handleGroupResourceError(index)"
              />
            </div>
          </div>
          <div v-else class="empty-block">
            {{ selectedDisease ? 'Select grouping, then click Browse.' : 'Select disease first to enable grouping.' }}
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import request, { downloadFile } from '@/utils/request'

export default {
  name: 'Survival',
  data() {
    return {
      selectedCategory: '',
      selectedDisease: '',
      diseaseKeyword: '',
      selectedGrouping: '',
      categories: [],
      diseases: [],
      groupingOptions: [
        { label: '(0-5%, 5-95%, 95-100%)', value: 'p5p95' },
        { label: '(0-33%, 33-67%, 67-100%)', value: 'p33p67' },
        { label: '(0-20%, 20-80%, 80-100%)', value: 'p20p80' }
      ],
      sex: 'all',
      tableColumns: [],
      tableData: [],
      tableLoading: false,
      sortOrders: ['descending', 'ascending', null],
      sortKey: '',
      sortOrder: '',
      groupResourceLoading: false,
      groupResourceImages: [],
      currentPage: 1,
      pageSize: 10,
      total: 0,
      totalPages: 0
    }
  },
  watch: {
    selectedCategory(newVal) {
      if (newVal) {
        this.loadDiseases(newVal)
        this.selectedDisease = ''
        this.selectedGrouping = ''
        this.groupResourceImages = []
      } else {
        this.diseases = []
        this.selectedDisease = ''
        this.selectedGrouping = ''
        this.groupResourceImages = []
      }
    },
    selectedDisease() {
      this.selectedGrouping = ''
      this.groupResourceImages = []
    },
    selectedGrouping(newVal) {
      this.groupResourceImages = []
      if (newVal && this.selectedDisease) {
        this.loadGroupResources()
      }
    }
  },
  mounted() {
    this.loadCategories()
  },
  computed: {
    filteredDiseases() {
      if (!this.diseaseKeyword) {
        return this.diseases
      }
      const keyword = this.diseaseKeyword.toLowerCase()
      return this.diseases.filter(item =>
        (item.reportedTrait && item.reportedTrait.toLowerCase().includes(keyword)) ||
        (item.phid && item.phid.toLowerCase().includes(keyword))
      )
    },
    groupResourcePreviewList() {
      return this.groupResourceImages.map(item => item.src).filter(Boolean)
    }
  },
  methods: {
    async loadCategories() {
      try {
        const data = await request.get('/api/survival/categories')
        this.categories = data || []
      } catch (error) {
        console.error('Failed to load categories:', error)
      }
    },
    async loadDiseases(category) {
      try {
        const data = await request.get('/api/survival/diseases', {
          params: { category }
        })
        this.diseases = data || []
        this.diseaseKeyword = ''
      } catch (error) {
        console.error('Failed to load diseases:', error)
      }
    },
    filterDiseases(keyword) {
      this.diseaseKeyword = keyword
    },
    handleSearch() {
      if (!this.selectedCategory || !this.selectedDisease) {
        this.$message.warning('Please select both Disease Category and Disease.')
        return
      }
      this.currentPage = 1
      this.sortKey = ''
      this.sortOrder = ''
      this.loadTableData()
    },
    handlePageChange(page) {
      if (page < 1 || page > this.totalPages || page === this.currentPage) return
      this.currentPage = page
      this.loadTableData()
    },
    async loadTableData() {
      if (!this.selectedDisease) return
      this.tableLoading = true
      try {
        const data = await request.get('/api/survival/summary/page', {
          params: {
            phid: this.selectedDisease,
            sex: this.sex,
            pageNum: this.currentPage,
            pageSize: this.pageSize,
            sortBy: this.sortKey || undefined,
            sortOrder: this.sortOrder || undefined
          }
        })
        const headers = data.headers || []
        const records = data.records || []
        this.tableColumns = headers.map(header => ({
          key: header,
          label: header
        }))
        this.tableData = records.map(row => {
          const item = {}
          headers.forEach((header, index) => {
            item[header] = row[index]
          })
          return item
        })
        this.total = data.total || 0
        this.totalPages = data.pages || 0
      } catch (error) {
        console.error('Failed to load survival summary:', error)
        this.tableColumns = []
        this.tableData = []
        this.total = 0
        this.totalPages = 0
      } finally {
        this.tableLoading = false
      }
    },
    async handleDownloadSummary() {
      if (!this.selectedCategory || !this.selectedDisease) {
        this.$message.warning('Please select both Disease Category and Disease.')
        return
      }
      await downloadFile('/api/survival/summary/download', {
        phid: this.selectedDisease,
        sex: this.sex
      }, `survival_summary_${this.selectedDisease}_${this.sex}.tsv`)
    },
    handleSortChange({ prop, order }) {
      this.sortKey = order ? prop : ''
      this.sortOrder = this.normalizeElementSortOrder(order)
      this.currentPage = 1
      this.loadTableData()
    },
    normalizeElementSortOrder(order) {
      if (order === 'ascending') return 'asc'
      if (order === 'descending') return 'desc'
      return ''
    },
    async loadGroupResources() {
      if (!this.selectedDisease || !this.selectedGrouping) {
        this.groupResourceImages = []
        return
      }

      this.groupResourceLoading = true
      try {
        const data = await request.get('/api/survival/group-resource', {
          params: {
            phid: this.selectedDisease,
            group: this.selectedGrouping
          }
        })
        const resources = this.normalizeGroupResourceList(data)
        this.groupResourceImages = resources.map(this.normalizeImageResource).filter(Boolean)
      } catch (error) {
        console.error('Failed to load grouping resources:', error)
        this.groupResourceImages = []
      } finally {
        this.groupResourceLoading = false
      }
    },
    normalizeGroupResourceList(data) {
      if (!data) return []
      if (Array.isArray(data)) return data
      if (Array.isArray(data.data)) return data.data
      return [data]
    },
    normalizeImageResource(item) {
      if (!item) return null

      if (typeof item === 'string') {
        const payload = item.trim()
        return payload
          ? { name: '', src: payload.startsWith('data:image') ? payload : `data:image/png;base64,${payload}` }
          : null
      }

      const type = this.normalizeImageType(item.type || item.contentType)
      const payload = item.base64 || item.data || item.content || item.url || (
        item.resource && typeof item.resource === 'object'
          ? (item.resource.data || item.resource.base64 || item.resource.content || item.resource.url || '')
          : item.resource || ''
      )

      if (!payload || typeof payload !== 'string') return null

      const normalizedPayload = payload.trim().replace(/\s+/g, '')
      if (!normalizedPayload) return null

      return {
        name: item.name || '',
        src: normalizedPayload.startsWith('data:image')
          ? normalizedPayload
          : (/^(https?:)?\/\//.test(normalizedPayload) || normalizedPayload.startsWith('/'))
            ? normalizedPayload
            : `data:${type};base64,${normalizedPayload}`
      }
    },
    normalizeImageType(type) {
      if (!type || typeof type !== 'string') return 'image/png'
      const normalizedType = type.trim().toLowerCase()
      return normalizedType.startsWith('image/') ? normalizedType : 'image/png'
    },
    handleGroupResourceError(index) {
      this.groupResourceImages.splice(index, 1)
    }
  }
}
</script>

<style lang="less" scoped>
.survival {
  min-height: 100%;
  position: relative;
  overflow: hidden;
  background: linear-gradient(180deg, #fef2f2 0%, #ffffff 100%);
}

.bg-decoration {
  position: absolute;
  inset: 0;
  pointer-events: none;
  overflow: hidden;
}

.molecule-bg {
  position: absolute;
  width: 400px;
  height: 400px;
  top: 20px;
  left: -50px;
}

.dna-bg {
  position: absolute;
  width: 200px;
  height: 500px;
  top: -50px;
  right: 0;
}

.survival-content {
  position: relative;
  z-index: 1;
  margin: 0 auto;
  padding: 48px 32px 64px;
  min-height: calc(100vh - 64px);
}

.header-section {
  text-align: center;
  margin-bottom: 32px;
}

.page-title {
  font-size: 40px;
  font-weight: 700;
  color: #1f2937;
  margin: 0 0 16px;
  line-height: 1.3;
}

.page-subtitle {
  font-size: 16px;
  color: #6b7280;
  margin: 0;
}

.query-card,
.table-card {
  background: rgba(255, 255, 255, 0.92);
  border: 1px solid rgba(252, 165, 165, 0.35);
  border-radius: 24px;
  box-shadow: 0 20px 45px rgba(153, 27, 27, 0.08);
  backdrop-filter: blur(8px);
}

.query-card {
  padding: 24px;
  margin-bottom: 24px;
}

.section-description {
  margin: 0 0 18px;
  padding: 14px 16px;
  font-size: 14px;
  line-height: 1.7;
  color: #6b7280;
  background: linear-gradient(135deg, #fff7f7 0%, #fff1f2 100%);
  border: 1px solid rgba(252, 165, 165, 0.3);
  border-radius: 14px;
}

.grouping-select-row {
  margin-bottom: 16px;
}

.filters-section {
  display: flex;
  align-items: flex-end;
  gap: 20px;
  flex-wrap: wrap;
}

.filter-group {
  min-width: 220px;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.filter-group-wide {
  min-width: 320px;
}

.filter-label {
  font-size: 13px;
  font-weight: 600;
  color: #7f1d1d;
}

.filter-input,
.filter-select {
  width: 100%;
}

.filter-actions {
  display: flex;
  align-items: center;
}

.search-btn {
  min-width: 120px;
  height: 36px;
  border-radius: 10px;
}

.table-card {
  padding: 24px;
}

.grouping-resource-card {
  margin-top: 24px;
}

.panel-header {
  display: flex;
  justify-content: space-between;
  gap: 16px;
  align-items: flex-end;
  margin-bottom: 20px;
  flex-wrap: wrap;
}

.panel-title {
  font-size: 22px;
  font-weight: 700;
  color: #1f2937;
}

.panel-subtitle,
.table-summary {
  font-size: 14px;
  color: #6b7280;
  margin-top: 6px;
}

.result-actions {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
  justify-content: flex-end;
}

:deep(.result-actions .el-button) {
  padding: 9px 16px;
  background: linear-gradient(135deg, #fff1f2 0%, #ffe4e6 100%);
  color: #991b1b;
  border: 1px solid #fecaca;
  border-radius: 10px;
  box-shadow: 0 6px 16px rgba(239, 68, 68, 0.12);
  font-weight: 600;
}

:deep(.result-actions .el-button:hover),
:deep(.result-actions .el-button:focus) {
  background: linear-gradient(135deg, #fee2e2 0%, #fecdd3 100%);
  border-color: #fca5a5;
  color: #7f1d1d;
}

.result-table {
  width: 100%;
}

.group-resource-wrap {
  min-height: 180px;
}

.images-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
  gap: 16px;
}

.image-card {
  border: 1px solid rgba(252, 165, 165, 0.35);
  border-radius: 16px;
  padding: 12px;
  background: #fffafa;
}

.image-name {
  font-size: 14px;
  font-weight: 600;
  color: #7f1d1d;
  margin-bottom: 10px;
}

.result-image {
  width: 100%;
  display: block;
  border-radius: 10px;
  background: #ffffff;
}

.preview-image {
  cursor: pointer;
}

:deep(.preview-image .el-image__inner) {
  display: block;
  width: 100%;
  border-radius: 10px;
}

.empty-block {
  min-height: 160px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #6b7280;
  background: #fffafa;
  border: 1px dashed #fecaca;
  border-radius: 16px;
}

.pagination-wrap {
  display: flex;
  justify-content: flex-end;
  margin-top: 20px;
}

:deep(.table-header-cell) {
  background: #fef2f2;
  color: #7f1d1d;
  font-weight: 700;
}

:deep(.el-table) {
  border-radius: 16px;
  overflow: hidden;
}

:deep(.el-table th.el-table__cell) {
  background: #fef2f2;
}

:deep(.el-pagination.is-background .btn-prev),
:deep(.el-pagination.is-background .btn-next),
:deep(.el-pagination.is-background .el-pager li) {
  background: #fff5f5;
  color: #7f1d1d;
  border: 1px solid #fecaca;
}

:deep(.el-pagination.is-background .el-pager li:not(.disabled).active) {
  background: #dc2626;
  color: #ffffff;
  border-color: #dc2626;
}

@media (max-width: 768px) {
  .survival-content {
    padding: 32px 16px 40px;
  }

  .page-title {
    font-size: 30px;
  }

  .query-card,
  .table-card {
    padding: 16px;
    border-radius: 18px;
  }

  .panel-header {
    align-items: flex-start;
  }

  .pagination-wrap {
    justify-content: center;
  }
}
</style>
